<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="920px"
    top="5vh"
    append-to-body
    custom-class="student-qr-dialog"
    :close-on-click-modal="false"
    @close="handleClose">
    <span slot="title" class="sq-dialog-title">
      <span class="sq-dialog-title-main">学生二维码</span>
      <span v-if="departmentName" class="sq-dialog-title-dept">【{{ departmentName }}】</span>
      <span v-else class="sq-dialog-title-dept sq-dialog-title-dept--muted">【全部】</span>
    </span>

    <div class="sq-toolbar">
      <div class="sq-toolbar-row">
        <span class="sq-label">二维码类型：</span>
        <el-radio-group v-model="qrType" size="small" @change="onQrTypeChange">
          <el-radio-button :label="QR_TYPE_STU_CODE">学号</el-radio-button>
          <el-radio-button :label="QR_TYPE_STUDENT_ID">学生ID</el-radio-button>
        </el-radio-group>
        <el-input
          v-model="stuNameKeyword"
          size="small"
          clearable
          placeholder="姓名"
          class="sq-search-input"
          @keyup.enter.native="handleSearch">
        </el-input>
        <el-input
          v-model="cardNoKeyword"
          size="small"
          clearable
          placeholder="卡号"
          class="sq-search-input"
          @keyup.enter.native="handleSearch">
        </el-input>
        <el-button size="small" type="primary" :loading="listLoading" @click="handleSearch">查询</el-button>
        <el-button size="small" type="success" :loading="exporting" :disabled="!canExport" @click="handleExportZip">导出 ZIP</el-button>
        <span v-if="totalCount > 0" class="sq-summary">
          共 <strong>{{ totalCount }}</strong> 人 · 本页 <strong>{{ pageStudentCount }}</strong> 人
          <template v-if="qrType === QR_TYPE_STU_CODE"> · 有学号 <strong>{{ pageQrCount }}</strong> 人</template>
          · 可生成 <strong>{{ pageQrCount }}</strong> 张
        </span>
      </div>
      <div class="sq-meta sq-meta--warn" v-if="limitError">
        <i class="el-icon-warning"></i> {{ limitError }}
      </div>
      <div class="sq-meta sq-meta--progress" v-if="exportProgress">{{ exportProgress }}</div>
    </div>

    <div v-loading="listLoading || previewLoading" class="sq-preview-wrap">
      <el-empty v-if="!departmentId" description="请先在左侧选择部门"></el-empty>
      <el-empty
        v-else-if="!listLoading && !previewLoading && studentRows.length === 0"
        description="暂无学生数据">
      </el-empty>
      <div v-else class="sq-grid">
        <div
          v-for="item in previewItems"
          :key="item.key"
          class="sq-card">
          <div class="sq-card-head">{{ item.departmentName }} · {{ item.stuName }}</div>
          <div class="sq-card-body">
            <div class="sq-qr-block">
              <img v-if="item.qrUrl" :src="item.qrUrl" class="sq-qr-img" :alt="qrTypeLabel + '二维码'" />
              <div v-else-if="!item.canGenerate" class="sq-qr-placeholder sq-qr-placeholder--empty">无学号</div>
              <div v-else class="sq-qr-placeholder">生成中…</div>
              <div class="sq-qr-label">{{ item.qrLabel }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="departmentId && totalCount > 0" class="sq-footer toolbar">
      <el-pagination
        layout="total,prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="totalCount"
        style="float: right"
        @current-change="loadPreview">
      </el-pagination>
    </div>
  </el-dialog>
</template>

<script>
import { getStudentInfoListPage } from '../api/modules/personnel';
import {
  MAX_STUDENT_QR_COUNT,
  QR_TYPE_STU_CODE,
  QR_TYPE_STUDENT_ID,
  buildOverLimitMessage,
  canGenerateQr,
  countQrImages,
  downloadBlob,
  exportStudentsQrZip,
  generateQrDataUrl,
  getQrContent,
  getQrTypeLabel,
  normalizeStudent
} from '../utils/studentQrExport'

export default {
  name: 'StudentQrCodeDialog',
  props: {
    visible: { type: Boolean, default: false },
    departmentId: { type: [String, Number], default: '' },
    departmentName: { type: String, default: '' },
    filters: { type: Object, default: function () { return {} } }
  },
  data: function () {
    return {
      QR_TYPE_STU_CODE: QR_TYPE_STU_CODE,
      QR_TYPE_STUDENT_ID: QR_TYPE_STUDENT_ID,
      qrType: QR_TYPE_STU_CODE,
      stuNameKeyword: '',
      cardNoKeyword: '',
      page: 1,
      pageSize: 12,
      totalCount: 0,
      studentRows: [],
      previewItems: [],
      listLoading: false,
      previewLoading: false,
      exporting: false,
      exportProgress: '',
      limitError: ''
    }
  },
  computed: {
    dialogVisible: {
      get: function () { return this.visible },
      set: function (val) { this.$emit('update:visible', val) }
    },
    pageStudentCount: function () {
      return (this.studentRows || []).length
    },
    pageQrCount: function () {
      return countQrImages(this.studentRows, this.qrType)
    },
    qrTypeLabel: function () {
      return getQrTypeLabel(this.qrType)
    },
    canExport: function () {
      return (
        !!this.departmentId &&
        this.totalCount > 0 &&
        !this.limitError &&
        !this.exporting &&
        !this.listLoading
      )
    }
  },
  watch: {
    visible: function (val) {
      if (val) this.initDialog()
    }
  },
  methods: {
    handleClose: function () {
      this.resetState()
    },
    resetState: function () {
      this.qrType = QR_TYPE_STU_CODE
      this.stuNameKeyword = ''
      this.cardNoKeyword = ''
      this.page = 1
      this.totalCount = 0
      this.studentRows = []
      this.previewItems = []
      this.limitError = ''
      this.exportProgress = ''
      this.listLoading = false
      this.previewLoading = false
      this.exporting = false
    },
    initDialog: function () {
      this.resetState()
      this.stuNameKeyword = (this.filters && this.filters.stuName) || ''
      this.cardNoKeyword = (this.filters && this.filters.cardNo) || ''
      this.loadPreview(1)
    },
    handleSearch: function () {
      this.page = 1
      this.loadPreview(1)
    },
    onQrTypeChange: function () {
      if (this.studentRows.length) {
        this.buildPreviewItems()
      }
    },
    buildQueryParams: function (page, pageSize) {
      const para = {
        page: page,
        departmentId: this.departmentId,
        intPageSize: pageSize
      }
      const stuName = (this.stuNameKeyword || '').trim()
      const cardNo = (this.cardNoKeyword || '').trim()
      if (stuName) para.stuName = stuName
      if (cardNo) para.cardNo = cardNo
      return para
    },
    validateTotalCount: function (count) {
      if (count > MAX_STUDENT_QR_COUNT) {
        this.limitError = buildOverLimitMessage(count)
        return false
      }
      this.limitError = ''
      return true
    },
    loadPreview: async function (page) {
      if (!this.departmentId) {
        this.studentRows = []
        this.previewItems = []
        this.totalCount = 0
        return
      }
      this.page = page || this.page || 1
      this.listLoading = true
      this.studentRows = []
      this.previewItems = []
      try {
        const res = await getStudentInfoListPage(this.buildQueryParams(this.page, this.pageSize))
        const body = res && res.data && res.data.response
        const rows = (body && body.data) || []
        this.totalCount = (body && body.dataCount) || 0
        this.validateTotalCount(this.totalCount)
        this.studentRows = rows
        if (rows.length) {
          await this.buildPreviewItems()
        }
      } catch (err) {
        console.error(err)
        this.$message.error('加载学生数据失败')
      } finally {
        this.listLoading = false
      }
    },
    fetchAllRowsForExport: async function () {
      const pageSize = 500
      let page = 1
      let allRows = []
      let totalCount = 0
      while (true) {
        const res = await getStudentInfoListPage(this.buildQueryParams(page, pageSize))
        const body = res && res.data && res.data.response
        const rows = (body && body.data) || []
        totalCount = (body && body.dataCount) || 0
        allRows = allRows.concat(rows)
        if (allRows.length >= totalCount || rows.length < pageSize) break
        page += 1
      }
      return { rows: allRows, totalCount: totalCount }
    },
    buildPreviewItems: async function () {
      this.previewLoading = true
      const self = this
      const normalized = this.studentRows.map(normalizeStudent)
      this.previewItems = normalized.map(function (s) {
        const canGen = canGenerateQr(s, self.qrType)
        const content = getQrContent(s, self.qrType)
        const labelPrefix = getQrTypeLabel(self.qrType)
        return {
          key: String(s.id),
          departmentName: s.departmentName,
          stuName: s.stuName,
          canGenerate: canGen,
          qrUrl: '',
          qrLabel: canGen ? (labelPrefix + '：' + content) : (labelPrefix + '：-')
        }
      })
      try {
        for (let i = 0; i < this.previewItems.length; i++) {
          const item = this.previewItems[i]
          const student = normalized[i]
          if (!item.canGenerate) continue
          item.qrUrl = await generateQrDataUrl(getQrContent(student, this.qrType))
          if ((i + 1) % 20 === 0) {
            await this.$nextTick()
          }
        }
      } catch (err) {
        console.error(err)
        this.$message.error('二维码预览生成失败')
      } finally {
        this.previewLoading = false
      }
    },
    handleExportZip: async function () {
      if (!this.departmentId) {
        this.$message.warning('请先在左侧选择部门')
        return
      }
      if (!this.totalCount) {
        this.$message.warning('没有可导出的学生')
        return
      }
      if (this.limitError) {
        return
      }
      this.exporting = true
      this.exportProgress = '正在加载学生数据…'
      try {
        const result = await this.fetchAllRowsForExport()
        if (!result.rows.length) {
          this.$message.warning('没有可导出的学生')
          return
        }
        const exportableCount = countQrImages(result.rows, this.qrType)
        if (!exportableCount) {
          this.$message.warning('当前筛选结果中没有可导出的' + this.qrTypeLabel + '二维码')
          return
        }
        if (!this.validateTotalCount(exportableCount)) {
          return
        }
        const self = this
        const zipResult = await exportStudentsQrZip(result.rows, this.qrType, function (done, total) {
          self.exportProgress = '正在生成 ' + done + ' / ' + total + '…'
        })
        downloadBlob(zipResult.blob, zipResult.folderName + '.zip')
        let msg = '已开始下载，共 ' + zipResult.fileCount + ' 张' + this.qrTypeLabel + '二维码'
        if (zipResult.skippedCount > 0) {
          msg += '，跳过 ' + zipResult.skippedCount + ' 人（无学号）'
        }
        this.$message.success(msg)
      } catch (err) {
        console.error(err)
        this.$message.error('导出失败，请缩小搜索范围后重试')
      } finally {
        this.exporting = false
        this.exportProgress = ''
      }
    }
  }
}
</script>

<style scoped>
.sq-dialog-title {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  line-height: 1.4;
}

.sq-dialog-title-main {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.sq-dialog-title-dept {
  font-size: 14px;
  font-weight: 500;
  color: var(--btn-primary, #2563eb);
}

.sq-dialog-title-dept--muted {
  color: #909399;
}

.sq-toolbar {
  margin-bottom: 14px;
}

.sq-toolbar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.sq-search-input {
  width: 140px;
}

.sq-label {
  color: #606266;
  font-size: 13px;
}

.sq-summary {
  margin-left: auto;
  font-size: 13px;
  color: #606266;
}

.sq-meta {
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
}

.sq-meta--warn {
  color: #f56c6c;
}

.sq-meta--progress {
  color: var(--btn-primary, #2563eb);
}

.sq-preview-wrap {
  max-height: 56vh;
  overflow: auto;
  padding: 2px 4px 8px 0;
}

.sq-footer.toolbar {
  margin-top: 12px;
  padding: 8px 0 0;
  overflow: hidden;
  border-top: 1px solid #eef2f7;
}

.sq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.sq-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 14px;
  background: #fafafa;
}

.sq-card-head {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.sq-card-body {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.sq-qr-block {
  text-align: center;
  flex: 0 0 auto;
}

.sq-qr-img {
  width: 140px;
  height: 140px;
  display: block;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e4e7ed;
}

.sq-qr-placeholder {
  width: 140px;
  height: 140px;
  line-height: 140px;
  text-align: center;
  color: #909399;
  font-size: 12px;
  background: #fff;
  border: 1px dashed #dcdfe6;
  margin: 0 auto;
}

.sq-qr-placeholder--empty {
  line-height: 1.4;
  padding-top: 58px;
  color: #c0c4cc;
  border-color: #ebeef5;
}

.sq-qr-label {
  margin-top: 6px;
  font-size: 12px;
  color: #606266;
  word-break: break-all;
}
</style>

<style>
/* 学生二维码：自管高度，不受全局 body 限高影响。最后修改：2026-09-14 */
.student-qr-dialog .el-dialog__body {
  max-height: none !important;
  padding: 14px 20px 16px !important;
  overflow: hidden;
}
.student-qr-dialog .el-dialog__header {
  padding: 14px 20px 12px;
  border-bottom: 1px solid #eef2f7;
}
</style>
